import * as React from "react";
import cx from "classnames";

import { Flex } from "@common/components/Flex";
import { trDts } from "@modules/datasheets/utils/i18n";
import { Checkbox } from "@common/components/checkbox";
import { getTestAttributes } from "@common/utils/tests";
import { TextArea } from "@common/components/text-area";
import { FormControl } from "@common/components/form-control";
import { Button } from "@common/components/Button";
import {
  DatasheetElementSettingsProps,
  useDatasheetElementSettingsController,
} from "./controllers/main";
import { TextStyleManagement } from "./components/text-style-management/text-style-management";
import { ConfigureTable } from "./components/configure-table/configure-table";

import "./datasheet-element-settings.styles.scss";

export const DatasheetElementSettings: React.FC<DatasheetElementSettingsProps> = (props) => {
  const { className, fieldComponentValue, textComponentValue, title } = props;
  const {
    currentFieldComponentValue,
    contentController,
    headingController,
    tableController,
    addedTextController,
    textStyleOptions,
    textAlignOptions,
    fontFamilyOptions,
    fontSizesOptions,
    headingAlignOptions,
    expanded,
    toggleExpanded,
    handleHelpTextChange,
  } = useDatasheetElementSettingsController(props);

  const fieldSelected = Boolean(fieldComponentValue);

  return (
    <div
      className={cx(
        "DatasheetsEditor__datasheet-element-settings",
        { "DatasheetsEditor__datasheet-element-settings--expanded": expanded },
        className
      )}
    >
      <Flex>
        <button
          {...getTestAttributes({ id: "fp-datasheet-design-right-panel-toggle-btn" })}
          className="DatasheetsEditor__datasheet-element-settings__toggler"
          onClick={toggleExpanded}
        >
          <img src={expanded ? "png/arrow_right.png" : "png/arrow_left.png"} />
        </button>
        {expanded && (
          <p className="DatasheetsEditor__datasheet-element-settings__title">{title || (textComponentValue ? trDts('Added Text') : trDts("Default Settings"))}</p>
        )}
      </Flex>

      <div className="DatasheetsEditor__datasheet-element-settings__content">
        <TextStyleManagement
          fontFamilyOptions={fontFamilyOptions}
          fontFamily={currentFieldComponentValue.content.fontFamily}
          color={currentFieldComponentValue.content.color}
          fontSizesOptions={fontSizesOptions}
          fontSize={currentFieldComponentValue.content.fontSize}
          headingAlignOptions={headingAlignOptions}
          textStyles={currentFieldComponentValue.content.styles}
          headingPositionOptions={headingController.positions}
          textStylesOptions={textStyleOptions}
          textAlignOptions={textAlignOptions}
          textAlign={currentFieldComponentValue.content.align}
          onFontFamilyChange={contentController.handleFontFamilyChange}
          onColorChange={contentController.handleColorChange}
          onFontSizeChange={contentController.handleFontSizeChange}
          onTextStylesChange={contentController.handleStylesChange}
          onTextAlignsChange={contentController.handleAlignChange}
          getId={{
            font: getTestAttributes({ id: "data-font-list" }).id,
            fontSize: getTestAttributes({ id: "data-font-size-list" }).id,
            colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-datacolorbtn" }).id,
          }}
        />

        {fieldSelected && (
          <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
            <span style={{ marginRight: "4px" }}>{trDts("Tab Order")}</span>
            <FormControl
              {...getTestAttributes({ id: "tx-datasheet-design-right-panel-tab-order" })}
              value={fieldComponentValue?.content.tabOrder}
              type="number"
              style={{ width: "80px" }}
              onChange={(e) => contentController.handleTabOrderChange(parseInt(e.target.value))}
            />
          </Flex>
        )}
        <div>
          <Checkbox
            {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-border-input" })}
            checked={currentFieldComponentValue.content.showBorder}
            label={trDts("Show Border")}
            onChange={contentController.handleShowBorderChange}
          />
          <Checkbox
            {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-field-type-input" })}
            checked={currentFieldComponentValue.content.showFieldType}
            label={trDts("Show Field Type")}
            onChange={contentController.handleShowFieldTypeChange}
          />
        </div>
        {fieldComponentValue?.table && (
          <Button
            {...getTestAttributes({ id: "btn-datasheet-design-right-panel-configure-table" })}
            onClick={tableController.openConfigureTable}
            bsStyle="primary"
          >
            {trDts("Configure Table")}
          </Button>
        )}

        {tableController.configureTableOpened && (
          <ConfigureTable
            title={"Configure Table Paternal Ancestry Table"}
            dropdownFormats={tableController.formats}
            tableFormats={tableController.tableFormats}
            onClose={tableController.closeConfigureTable}
            onSubmit={tableController.handleSubmitConfigureTable}
            checkFormatNameExists={tableController.checkFormatNameExists}
          />
        )}

        <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Heading")}</h3>
        <div className="DatasheetsEditor__datasheet-element-settings__section">
          <Checkbox
            {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-display-input" })}
            checked={currentFieldComponentValue.heading.displayHeading}
            label={trDts("Display Heading")}
            onChange={headingController.handleDisplayHeadingChange}
          />
        </div>
        {fieldSelected && (
          <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
            <span style={{ marginRight: "4px" }}>{trDts("Heading")}</span>
            <FormControl
              {...getTestAttributes({ id: "tx-datasheet-design-right-panel-heading-input" })}
              value={fieldComponentValue?.heading.textValue}
              style={{ marginLeft: "4px" }}
              onChange={(e) => headingController.handleTextValueChange(e.target.value)}
            />
          </Flex>
        )}

        <TextStyleManagement
          fontFamilyOptions={fontFamilyOptions}
          fontSizesOptions={fontSizesOptions}
          textStylesOptions={textStyleOptions}
          fontFamily={currentFieldComponentValue.heading.fontFamily}
          color={currentFieldComponentValue.heading.color}
          headingAlign={currentFieldComponentValue.heading.align}
          headingAlignOptions={headingAlignOptions}
          fontSize={currentFieldComponentValue.heading.fontSize}
          headingPosition={currentFieldComponentValue.heading.position}
          headingPositionOptions={headingController.positions}
          textStyles={currentFieldComponentValue.heading.textStyles}
          onFontFamilyChange={headingController.handleFontFamilyChange}
          onColorChange={headingController.handleColorChange}
          onHeadingAlignChange={headingController.handleAlignChange}
          onFontSizeChange={headingController.handleFontSizeChange}
          onHeadingPositionChange={headingController.handlePositionChange}
          onTextStylesChange={headingController.handleStylesChange}
          getId={{
            font: getTestAttributes({ id: "head-font-list" }).id,
            fontSize: getTestAttributes({ id: "head-font-size-list" }).id,
            colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-head-color-btn" }).id,
            positions: getTestAttributes({ id: "head-position-list" }).id,
            aligns: getTestAttributes({ id: "align-list" }).id,
          }}
        />

        {textComponentValue && (
          <>
            <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Added Text")}</h3>
            <TextStyleManagement
              fontFamilyOptions={fontFamilyOptions}
              fontSizesOptions={fontSizesOptions}
              textStylesOptions={textStyleOptions}
              fontFamily={textComponentValue.fontFamily}
              headingAlignOptions={headingAlignOptions}
              color={textComponentValue.color}
              headingPositionOptions={headingController.positions}
              fontSize={textComponentValue.fontSize}
              textStyles={textComponentValue.textStyle}
              onFontFamilyChange={addedTextController.handleFontFamilyChange}
              onColorChange={addedTextController.handleColorChange}
              onTextStylesChange={addedTextController.handleStylesChange}
              onFontSizeChange={addedTextController.handleFontSizeChange}
              getId={{
                font: getTestAttributes({ id: "text-font-list" }).id,
                fontSize: getTestAttributes({ id: "text-font-size-list" }).id,
                colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-text-color-btn" }).id,
              }}
            />
          </>
        )}
        {fieldSelected && (
          <>
            <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Help Text")}</h3>
            <TextArea
              {...getTestAttributes({ id: "tx-datasheet-design-right-panel-help-text-input" })}
              value={currentFieldComponentValue.helpText.textValue}
              onChange={(e) => handleHelpTextChange(e.target.value)}
              style={{ minHeight: "200px" }}
            />
          </>
        )}
      </div>
    </div>
  );
};
